import React from 'react'
import styled from 'styled-components'
import {
  getDemographicsLegendConfig,
  isGradientLegend,
  isCategoricalLegend,
} from '../config/demographicsLegendConfig'

export interface DemographicsLegendProps {
  layerId: string
  className?: string
}

/**
 * Demographics Legend Component
 *
 * Displays either a gradient (continuous) legend or categorical (discrete) legend
 * based on the demographics layer type.
 */
export const DemographicsLegend: React.FC<DemographicsLegendProps> = ({
  layerId,
  className,
}) => {
  const config = getDemographicsLegendConfig(layerId)

  if (!config) {
    return null
  }

  return (
    <LegendContainer className={className}>
      <LegendTitle>{config.label}</LegendTitle>

      {isGradientLegend(config) && (
        <GradientLegend>
          <GradientBar
            $colors={config.stops.map((stop) => stop.color)}
            $positions={config.stops.map(
              (_, index) => (index / (config.stops.length - 1)) * 100
            )}
          />
          <GradientLabels>
            {config.stops.map((stop, index) => (
              <GradientLabel
                key={index}
                $position={(index / (config.stops.length - 1)) * 100}
              >
                {stop.label}
              </GradientLabel>
            ))}
          </GradientLabels>
          <UnitLabel>{config.unit}</UnitLabel>
        </GradientLegend>
      )}

      {isCategoricalLegend(config) && (
        <CategoricalLegend>
          {config.categories.map((category) => (
            <CategoryItem key={category.value}>
              <CategoryColor $color={category.color} />
              <CategoryLabel>{category.label}</CategoryLabel>
            </CategoryItem>
          ))}
        </CategoricalLegend>
      )}
    </LegendContainer>
  )
}

// Styled Components

const LegendContainer = styled.div`
  padding: 8px 0;
  width: 100%;
`

const LegendTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
`

// Gradient Legend Styles

const GradientLegend = styled.div`
  width: 100%;
`

const GradientBar = styled.div<{ $colors: string[]; $positions: number[] }>`
  height: 12px;
  border-radius: 2px;
  background: ${({ $colors, $positions }) => {
    if ($colors.length === 0) return '#CCCCCC'
    if ($colors.length === 1) return $colors[0]

    const stops = $colors
      .map((color, index) => `${color} ${$positions[index]}%`)
      .join(', ')
    return `linear-gradient(to right, ${stops})`
  }};
  margin-bottom: 4px;
`

const GradientLabels = styled.div`
  position: relative;
  height: 16px;
  margin-top: 2px;
`

const GradientLabel = styled.div<{ $position: number }>`
  position: absolute;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  font-size: 9px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;

  &:first-child {
    transform: none;
    left: 0;
  }

  &:last-child {
    transform: none;
    left: auto;
    right: 0;
  }
`

const UnitLabel = styled.div`
  font-size: 9px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-align: right;
  margin-top: 2px;
`

// Categorical Legend Styles

const CategoricalLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const CategoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CategoryColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`

const CategoryLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`
